<template>
  <div
    class="default-layout-container"
    :style="colors"
    v-if="!isFetching"
    :class="sidebarDisplay ? 'active' : 'disable'"
  >
    <div class="side-bar-section">
      <side-bar />
      <div class="sidebar-toggler" @click="sidebarDisplay = !sidebarDisplay">
        <i class="fa-solid fa-bars" />
      </div>
    </div>

    <div class="content-section" v-if="!isFetching">
      <div class="header-section">
        <Header @language="language = $event" />
      </div>
      <div class="router-section">
        <page-info-panel v-if="!language" />
        <router-view v-if="!language" />
      </div>
      <div class="footer-section">
        <Footer />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import Footer from '@/components/Defaults/Footer.vue'
  import Header from '@/components/Defaults/Header.vue'
  import SideBar from '@/components/Defaults/SideBar.vue'
  import PageInfoPanel from '@/components/Shared/Helpers/PageInfoPanel.vue'

  import { useAuthStore, useDataStore } from '@/pinia'
  import { HexToRGB, PermissionController } from '@/utils-ts'
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

  const [authStore, dataStore] = [useAuthStore(), useDataStore()]

  const brandColor = ref<string>('#8C3C84')
  const sidebarColor = ref<string>('#FFFFFF')
  const textColor = ref<string>('#8C3C84')
  const isFetching = ref<boolean>(true)
  const sidebarDisplay = ref<boolean>(true)
  const language = ref<boolean>(false)

  const companyData = computed(() => dataStore.companyInfo)

  const colors = computed(() => {
    const isDefaultColor = companyData.value.isDefaultColor
    const colors = {
      brand: isDefaultColor ? brandColor.value : companyData.value.colorBrand,
      sidebar: isDefaultColor ? sidebarColor.value : companyData.value.colorHeader,
      text: isDefaultColor ? textColor.value : companyData.value.colorHeaderText,
    }
    const brandColorRGB = HexToRGB(colors.brand)
    const textColorRGB = HexToRGB(colors.text)
    const sidebarColorRGB = HexToRGB(colors.sidebar)
    return {
      '--brand-color': colors.brand,
      '--brand-color-rgb': `${brandColorRGB?.r}, ${brandColorRGB?.g}, ${brandColorRGB?.b}`,

      '--text-color': colors.text,
      '--text-color-rgb': `${textColorRGB?.r}, ${textColorRGB?.g}, ${textColorRGB?.b}`,

      '--sidebar-color': colors.sidebar,
      '--sidebar-color-rgb': `${sidebarColorRGB?.r}, ${sidebarColorRGB?.g}, ${sidebarColorRGB?.b}`,
    }
  })

  // body'ye teleport edilen içerik (date picker menüsü, görsel önizleme) layout div'inin
  // değişkenlerini göremez; aynı değişkenler :root'a da yazılır.
  watch(
    colors,
    value => {
      Object.entries(value).forEach(([key, val]) => {
        document.documentElement.style.setProperty(key, val)
      })
    },
    { immediate: true },
  )

  onMounted(async () => {
    await dataStore.GetPermissionByJwt()
    await authStore.GetEmployeeByIdWeb()
    await dataStore.GetCompanyInfo()
    if (!companyData.value.isDefaultColor) {
      brandColor.value = companyData.value.colorBrand
      sidebarColor.value = companyData.value.colorHeader
      textColor.value = companyData.value.colorHeaderText
    }
    isFetching.value = false
    window.addEventListener('resize', resizeHandler)

    if (await PermissionController('admin_pages_others_chatbot')) {
      InsertChatbotScript()
    }
  })

  onUnmounted(() => {
    window.removeEventListener('resize', resizeHandler)
  })

  const resizeHandler = () => {
    if (window.innerWidth < 768) {
      sidebarDisplay.value = false
    } else {
      sidebarDisplay.value = true
    }
  }

  const InsertChatbotScript = () => {
    // 1. Global değişkeni tanımla
    ;(window as any)._pproject = (window as any)._pproject || []
    ;(window as any)._pproject.push(['_project_token', '7faea7dc-f717-4441-8387-a1cb66ecef50'])

    // 2. Script'i DOM'a ekle
    const pp = document.createElement('script')
    pp.type = 'text/javascript'
    pp.defer = true
    pp.src = 'https://public.palmate.ai/iframe/popup.js'

    const s = document.getElementsByTagName('script')[0]
    if (s && s.parentNode) {
      s.parentNode.insertBefore(pp, s)
    } else {
      document.head.appendChild(pp)
    }
  }
</script>

<style lang="scss" scoped>
  .default-layout-container {
    display: flex;
    flex-direction: row;
    height: auto;
    width: 100%;
    position: relative;
    transition: 0.2s ease;
    &.active {
      .side-bar-section {
        margin-left: 0;
      }
      .content-section {
        width: calc(100% - 275px);
        margin-left: 275px;
      }
    }
    &.disable {
      .side-bar-section {
        margin-left: -275px;
      }
      .content-section {
        width: 100%;
        margin-left: 0;
      }
    }

    .side-bar-section {
      width: 275px;
      height: 100vh;
      position: fixed;
      transition: 0.2s ease;
      z-index: 9;
      .sidebar-toggler {
        top: 0;
        right: -1.5rem;
        width: 1.5rem;
        height: 1.5rem;
        position: absolute;
        @include flexible($justify: center, $align: center);
        background-color: $textColor;
        color: $white;
      }
    }
    .content-section {
      width: calc(100% - 275px);
      margin-left: 275px;
      height: 100%;
      display: flex;
      flex-direction: column;
      transition: 0.2s ease;

      @include media(xs, sm, md) {
        width: 100%;
        margin-left: 0;
      }
      .header-section {
        width: 100%;
        height: 5rem;
        @include media(xs, sm, md) {
          height: 4rem;
        }
      }
      .router-section {
        width: 100%;
        height: auto;
        min-height: calc(100vh - 10rem);
        background-color: #fff;
        padding: 1rem;
        @include media(xs, sm, md) {
          min-height: calc(100vh - 8rem);
        }
      }
      .footer-section {
        width: 100%;
        height: 5rem;
        @include media(xs, sm, md) {
          height: 4rem;
        }
      }
    }
  }
</style>
